import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, RefreshCw, FileSpreadsheet, Printer, XCircle, Package } from 'lucide-react';
import Layout from '../../../components/Layout';
import { inventoryReportService, LowStockItemDTO } from '../../../services/inventoryReportService';
import { exportToCsv, printReport } from '../../../utils/exportUtils';
import './InventoryReportCommon.css';

const fmtQty = (n: number) => new Intl.NumberFormat().format(n);

const LowStockReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [hasSearched, setHasSearched] = useState(false);
  const [searching,   setSearching]   = useState(false);
  const [items,       setItems]       = useState<LowStockItemDTO[]>([]);

  const handleLoad = async () => {
    setSearching(true);
    setHasSearched(true);
    try {
      const res = await inventoryReportService.getLowStock();
      const d: LowStockItemDTO[] = (res.data as any)?.data ?? res.data ?? [];
      setItems(d);
    } catch { setItems([]); }
    finally { setSearching(false); }
  };

  const handleExcel = () => {
    exportToCsv(
      `low-stock-${new Date().toISOString().split('T')[0]}.csv`,
      ['SKU', 'Product', 'Category', 'Available Qty', 'Reorder Level', 'Shortage', 'Reorder Qty', 'Warehouses', 'Status'],
      items.map(i => [
        i.sku, i.productName, i.categoryName ?? '', i.availableQuantity,
        i.reorderLevel, i.shortage, i.reorderQuantity, i.warehouseNames ?? '',
        i.isOutOfStock ? 'Out of Stock' : 'Low Stock',
      ]),
    );
  };

  const outOfStock = items.filter(i => i.isOutOfStock).length;
  const lowStock   = items.filter(i => !i.isOutOfStock).length;

  return (
    <Layout>
      <div className="irp-page">

        <div className="irp-hero">
          <div>
            <h1 className="irp-hero-title"><AlertTriangle size={22} strokeWidth={1.8} /> Low Stock / Reorder Alert</h1>
            <p className="irp-hero-sub">Products at or below reorder level — action required</p>
          </div>
          <div className="irp-hero-actions">
            {hasSearched && items.length > 0 && (
              <div className="irp-export-group">
                <button className="irp-export-btn irp-export-excel" onClick={handleExcel}><FileSpreadsheet size={13} /> Export Excel</button>
                <button className="irp-export-btn irp-export-pdf" onClick={printReport}><Printer size={13} /> Print / PDF</button>
              </div>
            )}
            <button className="irp-back-btn" onClick={() => navigate('/reports/inventory')}><ArrowLeft size={14} /> Back</button>
          </div>
        </div>

        <div className="ims-card">
          <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
            <button className="ims-btn ims-btn-primary" onClick={handleLoad} disabled={searching}>
              {searching ? <><span className="irp-spinner" /> Loading…</> : <><RefreshCw size={14} /> Load Report</>}
            </button>
          </div>
        </div>

        {!hasSearched && (
          <div className="irp-prompt"><AlertTriangle size={48} strokeWidth={1} /><p>Click <strong>Load Report</strong> to view low stock items.</p></div>
        )}

        {hasSearched && !searching && (
          <>
            <div className="ims-stats-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-total"><Package size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{items.length}</span>
                  <span className="ims-stat-label">Total Alerts</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-out"><XCircle size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{outOfStock}</span>
                  <span className="ims-stat-label">Out of Stock</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-low"><AlertTriangle size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{lowStock}</span>
                  <span className="ims-stat-label">Low Stock</span>
                </div>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="ims-card"><div className="irp-empty">All items are adequately stocked.</div></div>
            ) : (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title"><AlertTriangle size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    Reorder Alerts ({items.length})
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="irp-table">
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Product</th>
                        <th>Category</th>
                        <th className="num">Available</th>
                        <th className="num">Reorder Lvl</th>
                        <th className="num">Shortage</th>
                        <th className="num">Suggest Order</th>
                        <th>Warehouses</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item.productId}>
                          <td><span className="irp-sku">{item.sku}</span></td>
                          <td style={{ fontWeight: 500 }}>{item.productName}</td>
                          <td>{item.categoryName ?? '—'}</td>
                          <td className={`num ${item.isOutOfStock ? 'irp-negative' : 'irp-value'}`}>
                            {fmtQty(item.availableQuantity)}
                          </td>
                          <td className="num">{fmtQty(item.reorderLevel)}</td>
                          <td className="num irp-negative">{fmtQty(item.shortage)}</td>
                          <td className="num irp-positive">{fmtQty(item.reorderQuantity)}</td>
                          <td style={{ fontSize: '0.78rem', color: 'var(--ims-text-sub)' }}>{item.warehouseNames ?? '—'}</td>
                          <td>
                            {item.isOutOfStock
                              ? <span className="ims-badge ims-badge-rejected">Out of Stock</span>
                              : <span className="ims-badge ims-badge-pending">Low Stock</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default LowStockReportPage;
